/* eslint-disable no-nested-ternary */
import { Box, Select, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useWorkspaceStore from "../../store/workspace/workspaceStore";
import StringDropdown from "../ui/dropdown/StringDropdown";
import { FILTER_COMBOBOX_PROPS, FILTER_SELECT_CLASSES } from "./filterStyles";

type Props = {
  handleChange: (
    operator: string,
    value: any,
    type: string,
    entityType: string,
    selectedField?: any
  ) => void;
  fieldSelect: boolean;
  op?: string;
  value?: any;
  selectedField?: any;
};

// Move constants outside component
const OPS = [
  { name: "is equal to", operator: "eq", id: 1 },
  { name: "is not equal to", operator: "ne", id: 2 },
  { name: "starts with", operator: "starts_with", id: 3 },
  { name: "ends with", operator: "ends_with", id: 4 },
  { name: "contains", operator: "contain", id: 5 },
  { name: "does not contain", operator: "does_not_contain", id: 6 },
  { name: "known", operator: "known", id: 7 },
  { name: "unknown", operator: "unknown", id: 8 },
  { name: "in", operator: "in", id: 9 },
];

const StringFilter = React.memo((props: Props) => {
  const { op, value, handleChange, fieldSelect, selectedField } = props;

  const workspaceId = useWorkspaceStore((state) => state.workspace?.id);

  const [selectOp, setSelectOp] = useState<any>(
    op ? OPS.find((item) => item.operator === op) : null
  );
  const [selectValue, setSelectValue] = useState<any>(value || "");
  const [searchValue, setSearchValue] = useState<string>(
    Array.isArray(value) ? "" : value || ""
  );
  const [selectedValues, setSelectedValues] = useState<string[]>(
    Array.isArray(value) ? value : []
  );
  const [inputValue, setInputValue] = useState("");
  const [debouncedVal] = useDebouncedValue(
    selectOp?.operator === "in" ? inputValue : selectValue,
    700
  );
  const [dropdown, setDropdown] = useState<boolean>(false);
  const inputRef = useRef<HTMLDivElement>(null);

  // Track refs to prevent loops
  const prevDebouncedValRef = useRef<any>(debouncedVal);
  const isUserInputRef = useRef(false);

  // Sync state with props (only update state, don't call handleChange)
  // Only depend on props, not state, to avoid loops
  useEffect(() => {
    if (op) {
      const foundOp = OPS.find((item) => item.operator === op);
      if (foundOp) {
        setSelectOp((prev: any) => {
          if (prev?.operator !== foundOp.operator) {
            return foundOp;
          }
          return prev;
        });
      }
    } else {
      setSelectOp((prev: any) => {
        if (prev) {
          return null;
        }
        return prev;
      });
    }
  }, [op]);

  useEffect(() => {
    // Only sync if not from user input
    if (!isUserInputRef.current) {
      if (Array.isArray(value)) {
        setSelectedValues(value);
        setInputValue("");
      } else {
        const newValue = value || "";
        setSelectValue((prev: string) => {
          if (prev !== newValue) {
            return newValue;
          }
          return prev;
        });
        setSearchValue((prev: string) => {
          if (prev !== newValue) {
            return newValue;
          }
          return prev;
        });
      }
    }
    isUserInputRef.current = false; // Reset flag
  }, [value]);

  // Fix event listener - use stable handler
  useEffect(() => {
    const currentElement = inputRef.current;
    if (!currentElement) return undefined;

    const handleClick = () => {
      setDropdown(true);
    };

    currentElement.addEventListener("click", handleClick);

    return () => {
      currentElement.removeEventListener("click", handleClick);
    };
  }, []);

  // Generate stable IDs
  const textInputId = useMemo(() => `string-filter-${selectedField || 'empty'}-${op || 'empty'}`, [selectedField, op]);
  const textInputId2 = useMemo(() => `string-filter-2-${selectedField || 'empty'}-${op || 'empty'}`, [selectedField, op]);

  // Call handleChange directly in onChange handlers (user interaction only)
  const handleOpChange = useCallback((val: string | null) => {
    const selected = OPS.find(item => item.id.toString() === val);
    setSelectOp(selected || null);
    // Call handleChange immediately for user interaction
    if (selected) {
      if (selected.operator === "known" || selected.operator === "unknown") {
        handleChange(selected.operator, "", "string", "column");
      } else if (selected.operator === "in") {
        handleChange(selected.operator, selectedValues, "string", "column");
      } else {
        handleChange(selected.operator, debouncedVal, "string", "column");
      }
    }
  }, [selectedValues, debouncedVal, handleChange]);

  const handleValueChange = useCallback((val: any) => {
    setSelectValue(val);
  }, []);

  const handleSearchValueChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    isUserInputRef.current = true; // Mark as user input
    setDropdown(true);
    setSearchValue(e.target.value);
  }, []);

  const handleSelectValueChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    isUserInputRef.current = true; // Mark as user input
    setSelectValue(e.target.value);
  }, []);

  const handleMultiSelectChange = useCallback((val: any) => {
    setSelectedValues(val);
    setInputValue("");
    // Call handleChange immediately for user interaction
    if (selectOp?.operator === "in") {
      handleChange(selectOp.operator, val, "string", "column");
    }
  }, [selectOp, handleChange]);

  // Use debounced value for handleChange (only when user types)
  useEffect(() => {
    // Only call if debounced value actually changed from user input
    if (prevDebouncedValRef.current === debouncedVal) return;

    if (
      selectOp?.operator &&
      selectOp?.operator !== "known" &&
      selectOp?.operator !== "unknown" &&
      selectOp?.operator !== "in"
    ) {
      prevDebouncedValRef.current = debouncedVal;
      handleChange(
        selectOp.operator,
        debouncedVal,
        "string",
        "column"
      );
    }
  }, [debouncedVal, selectOp?.operator]); // Removed handleChange from deps

  // Determine entity type
  const entityType = useMemo(
    () => selectedField?.includes("accounts.") ? "accounts" : "contacts",
    [selectedField]
  );

  // Render value input based on operator
  const renderValueInput = () => {
    if (selectOp?.operator === "known" || selectOp?.operator === "unknown") {
      return null;
    }

    if (selectOp?.operator === "in") {
      return (
        <Box>
          <div className="relative" ref={inputRef}>
            <StringDropdown
              isOpen={dropdown}
              setIsOpen={setDropdown}
              multiSelect
              field={selectedField}
              entityType={entityType}
              workspaceID={workspaceId}
              value={debouncedVal}
              setValue={handleMultiSelectChange}
              selectedValues={selectedValues}
            >
              <div
                onClick={() => setDropdown(true)}
                className="flex items-center min-w-[180px] justify-between w-full px-2 py-1.5 h-full text-xs text-foreground border border-border rounded-md hover:border-border/80 cursor-pointer bg-background"
              >
                {selectedValues.length > 0 ? (
                  <div className="flex items-center gap-1">
                    <span className="font-medium whitespace-nowrap text-foreground">
                      {selectedValues[0]}
                      {selectedValues.length > 1 && ` , ${selectedValues[1]}`}
                      {selectedValues.length > 2 && ` ...`}
                    </span>
                    {selectedValues.length > 2 && (
                      <span className="italic text-[10px] text-muted-foreground ml-3">
                        +{selectedValues.length - 2} more
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-muted-foreground">Select values...</span>
                )}
              </div>
            </StringDropdown>
          </div>
        </Box>
      );
    }

    if (selectOp?.operator === "eq" || selectOp?.operator === "ne") {
      return (
        <StringDropdown
          isOpen={dropdown}
          setIsOpen={setDropdown}
          field={selectedField}
          setSearchValue={setSearchValue}
          searchValue={searchValue}
          entityType={entityType}
          workspaceID={workspaceId}
          value={debouncedVal}
          setValue={handleValueChange}
        >
          <TextInput
            disabled={!selectOp?.operator}
            placeholder="Enter value here"
            id={textInputId}
            value={searchValue}
            type="text"
            classNames={{
              input: "bg-background text-foreground max-w-[155px] border-border",
            }}
            onChange={handleSearchValueChange}
            size="xs"
          />
        </StringDropdown>
      );
    }

    return (
      <TextInput
        classNames={{
          input: "bg-background text-foreground border-border",
        }}
        disabled={!selectOp?.operator}
        placeholder="Enter value here"
        id={textInputId2}
        value={selectValue}
        type="text"
        onChange={handleSelectValueChange}
        size="xs"
      />
    );
  };

  // Convert OPS to Select format
  const operatorOptions = useMemo(
    () => OPS.map(option => ({ value: option.id.toString(), label: option.name })),
    []
  );

  return (
    <>
      <Select
        classNames={FILTER_SELECT_CLASSES}
        comboboxProps={FILTER_COMBOBOX_PROPS}
        disabled={fieldSelect}
        placeholder="Select Condition"
        data={operatorOptions}
        value={selectOp ? selectOp.id.toString() : null}
        onChange={handleOpChange}
        size="xs"
        w={130}
      />
      {renderValueInput()}
    </>
  );
});

StringFilter.displayName = "StringFilter";

export default StringFilter;
